export const formatEnumValue = (enumValue: string): string => {
  if (!enumValue) return '';
  return enumValue
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const LOCAL_SHORT_DATE: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

/**
 * Parse an API UTC timestamp (ISO-8601, including Python microsecond precision)
 * into a Date. Instant is timezone-aware; calendar date should be read in local TZ.
 */
export const parseUtcTimestamp = (
  value: Date | string | null | undefined
): Date | null => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  let parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  // JS Date only guarantees millisecond precision; truncate extra fractional digits.
  const normalized = raw.replace(
    /(\.\d{3})\d+(Z|[+-]\d{2}:?\d{2})?$/i,
    "$1$2"
  );
  parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Calendar date of a UTC timestamp in the user's local timezone (e.g. "Aug 25, 2026"). */
export const formatUtcTimestampAsLocalDate = (
  value: Date | string | null | undefined
): string => {
  const parsed = parseUtcTimestamp(value);
  if (!parsed) return "";
  return parsed.toLocaleDateString("en-US", LOCAL_SHORT_DATE);
};

export const formatInsightShortDate = (
  value: Date | string | null | undefined
): string => {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", LOCAL_SHORT_DATE);
};

export const INSIGHT_UNKNOWN_DATE_LABEL = "Unknown date";

export type InsightDateDisplay = {
  label: string;
  dateTime?: string;
  isKnown: boolean;
};

export const getInsightDateDisplay = (
  value: Date | string | null | undefined
): InsightDateDisplay => {
  const label = formatInsightShortDate(value);
  if (label) {
    return {
      label,
      dateTime: toInsightDateTimeAttr(value),
      isKnown: true,
    };
  }
  return { label: INSIGHT_UNKNOWN_DATE_LABEL, isKnown: false };
};

export const toInsightDateTimeAttr = (
  value: Date | string | null | undefined
): string | undefined => {
  if (!value) return undefined;
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

/** Normalize Firestore Timestamp, ISO string, or epoch to Date. */
export const toDateFromFirestore = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === "object" && timestamp !== null) {
    const value = timestamp as {
      toDate?: () => Date;
      seconds?: number;
      nanoseconds?: number;
    };
    if (typeof value.toDate === "function") {
      return value.toDate();
    }
    if (typeof value.seconds === "number") {
      return new Date(value.seconds * 1000 + (value.nanoseconds ?? 0) / 1e6);
    }
  }
  const date = new Date(timestamp as string | number);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatTime = (timestamp: any): string => {
  if (!timestamp) return '';
  
  const date = toDateFromFirestore(timestamp);
  if (!date) return '';
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  
  const diffInDays = Math.floor(diffInMinutes / 1440);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`;
  
  return date.toLocaleDateString();
}; 